import { z } from "zod";

/**
 * `B3-PRICING-1` CONTRACT (`P0-CAT-042`, DEALER V2 B3-PRICING, D-1029-3): **the shapes the dealer's price door and vehicle read will speak, drawn before the implementation** (A48: the door and its
 * clients, `dealer-web` first, bind to one contract; no hand-written stub). Nothing here is served yet: `B3-PRICING-1`'s implementation serves it and flips `priceCeiling` from optional to required on the
 * vehicle read. Strict on purpose (an extra key is a mismatch, for stubs and the OpenAPI parity spec), each with a lenient READ twin built from the same shapes (the `bank-decision-answer.ts` pattern: an
 * unforeseen key is tolerated, a missing required field or a wrong type fails loudly).
 *
 * - **The ceiling** (`priceCeilingSchema`): while a dossier is open on a vehicle and has not been sent to a bank, the dealer may lower the price, or raise it back up to the price the dossier was opened at, never
 *   above. `openedAtAmount` is that price (whole francs); `dossierReference` the open dossier (`CR-…`). `null` on the vehicle read when no dossier is open (the dealer prices freely).
 * - **The ceiling refusal** (`POST /api/v1/me/vehicles/{id}/price` → `409 conflict`, `priceCeilingRefusalDetailsSchema`): `{ field: "priceAmount", ceilingAmount, dossierReference }`.
 * - **The lock** (same door → `409 conflict`): the price is locked while a dossier stands on the vehicle. `LOCK-STATE` (`P0-CAT-044`) gives both refusals a `reason` beside `{ dossierReference, stage }`:
 *   `priceLockedWhileOpenDetailsSchema` (`reason: "dossierOuvert"`, open and not sent) and `priceLockedAfterSendDetailsSchema` (`reason: "envoyeBanque"`, sent to a bank). The vehicle read serves the same lock as `priceLock`
 *   `{ reason, dossierReference } | null`, from the SAME predicate. The sentence is the server's (`message`); the client branches on `code` and these details, never on the words (4.5.5).
 */
function buildSchemas(object: typeof z.strictObject, sentenceOptional = false) {
  const wholeAmount = z.number().int();
  const text = z.string();

  const priceCeilingSchema = object({ openedAtAmount: wholeAmount, dossierReference: text });
  const priceCeilingRefusalDetailsSchema = object({
    field: z.literal("priceAmount"),
    ceilingAmount: wholeAmount,
    dossierReference: text,
  });
  /** `LOCK-STATE` (`P0-CAT-044`): why the price is locked, and the dossier that locks it. One predicate serves the vehicle read and the 409. */
  const priceLockSchema = object({
    reason: z.enum(["dossierOuvert", "envoyeBanque"]),
    dossierReference: text,
    /**
     * `PRICELOCK-SENTENCE`: the sentence the 409 sends, served on the read from the SAME composer (the server's words; the client draws them, composes nothing). Required when `priceLock` is present
     * (strict); the lenient read twin tolerates its absence (an older answer).
     */
    sentence: sentenceOptional ? text.optional() : text,
  });
  const priceLockedAfterSendDetailsSchema = object({
    reason: z.literal("envoyeBanque"),
    dossierReference: text,
    stage: text,
  });
  /** The sibling: a dossier is open and NOT yet sent (the sentence the door always carried). */
  const priceLockedWhileOpenDetailsSchema = object({
    reason: z.literal("dossierOuvert"),
    dossierReference: text,
    stage: text,
  });
  /** `POST /api/v1/me/vehicles/{id}/price`'s request. */
  const vehiclePriceChangeBodySchema = object({ priceAmount: wholeAmount });
  /** `POST /api/v1/me/vehicles/{id}/price`'s 200. */
  const vehiclePriceChangedAnswerSchema = object({
    id: text,
    priceAmount: wholeAmount,
    previousAmount: wholeAmount,
  });
  /** The 409 envelope body of the two refusals, `details` closed on each. */
  const refusalEnvelope = <T extends z.ZodType>(details: T) =>
    object({
      error: object({
        code: z.literal("conflict"),
        message: text.min(1),
        details,
        requestId: text,
        traceId: text,
      }),
    });
  return {
    priceLockSchema,
    priceLockedWhileOpenDetailsSchema,
    priceLockedWhileOpenRefusalSchema: refusalEnvelope(priceLockedWhileOpenDetailsSchema),
    priceCeilingSchema,
    priceCeilingRefusalDetailsSchema,
    priceLockedAfterSendDetailsSchema,
    vehiclePriceChangeBodySchema,
    vehiclePriceChangedAnswerSchema,
    priceCeilingRefusalSchema: refusalEnvelope(priceCeilingRefusalDetailsSchema),
    priceLockedAfterSendRefusalSchema: refusalEnvelope(priceLockedAfterSendDetailsSchema),
  };
}

const strict = buildSchemas(z.strictObject);
const loose = buildSchemas(z.looseObject, true);

export const priceLockSchema = strict.priceLockSchema;
export const priceLockedWhileOpenDetailsSchema = strict.priceLockedWhileOpenDetailsSchema;
export const priceLockedWhileOpenRefusalSchema = strict.priceLockedWhileOpenRefusalSchema;
export const priceCeilingSchema = strict.priceCeilingSchema;
export const priceCeilingRefusalDetailsSchema = strict.priceCeilingRefusalDetailsSchema;
export const priceLockedAfterSendDetailsSchema = strict.priceLockedAfterSendDetailsSchema;
export const vehiclePriceChangeBodySchema = strict.vehiclePriceChangeBodySchema;
export const vehiclePriceChangedAnswerSchema = strict.vehiclePriceChangedAnswerSchema;
export const priceCeilingRefusalSchema = strict.priceCeilingRefusalSchema;
export const priceLockedAfterSendRefusalSchema = strict.priceLockedAfterSendRefusalSchema;

/** The READ twins: extra keys tolerated at every level, every required field and type identical. */
export const priceLockReadSchema = loose.priceLockSchema;
export const priceLockedWhileOpenDetailsReadSchema = loose.priceLockedWhileOpenDetailsSchema;
export const priceLockedWhileOpenRefusalReadSchema = loose.priceLockedWhileOpenRefusalSchema;
export const priceCeilingReadSchema = loose.priceCeilingSchema;
export const priceCeilingRefusalDetailsReadSchema = loose.priceCeilingRefusalDetailsSchema;
export const priceLockedAfterSendDetailsReadSchema = loose.priceLockedAfterSendDetailsSchema;
export const vehiclePriceChangedAnswerReadSchema = loose.vehiclePriceChangedAnswerSchema;
export const priceCeilingRefusalReadSchema = loose.priceCeilingRefusalSchema;
export const priceLockedAfterSendRefusalReadSchema = loose.priceLockedAfterSendRefusalSchema;

export type PriceLock = z.infer<typeof priceLockSchema>;
export type PriceLockedWhileOpenDetails = z.infer<typeof priceLockedWhileOpenDetailsSchema>;
export type PriceCeiling = z.infer<typeof priceCeilingSchema>;
export type PriceCeilingRefusalDetails = z.infer<typeof priceCeilingRefusalDetailsSchema>;
export type PriceLockedAfterSendDetails = z.infer<typeof priceLockedAfterSendDetailsSchema>;
export type VehiclePriceChangeBody = z.infer<typeof vehiclePriceChangeBodySchema>;
export type VehiclePriceChangedAnswer = z.infer<typeof vehiclePriceChangedAnswerSchema>;
